
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface SavedProperty {
  id: string;
  user_id: string;
  property_id: string;
  created_at: string;
}

export const useWishlist = () => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState<SavedProperty[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setWishlist([]);
      setLoading(false);
    }
  }, [user]);

  const fetchWishlist = async () => {
    if (!user) {
      setWishlist([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('saved_properties_v2')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setWishlist(data || []);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setLoading(false);
    }
  };

  const isInWishlist = (propertyId: string): boolean => {
    return wishlist.some((item) => item.property_id === propertyId);
  };

  const toggleWishlist = async (propertyId: string): Promise<boolean> => {
    if (!user) {
      console.warn('toggleWishlist called without an authenticated user.');
      return false;
    }

    const currentlyInWishlist = isInWishlist(propertyId);

    try {
      if (currentlyInWishlist) {
        const { error } = await supabase
          .from('saved_properties_v2')
          .delete()
          .eq('user_id', user.id)
          .eq('property_id', propertyId);

        if (error) throw error;

        setWishlist((prev) =>
          prev.filter((item) => item.property_id !== propertyId)
        );
        return false;
      } else {
        const { data, error } = await supabase
          .from('saved_properties_v2')
          .insert({ user_id: user.id, property_id: propertyId })
          .select()
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setWishlist((prev) => [...prev, data]);
        }
        return true;
      }
    } catch (err) {
      console.error('Error toggling wishlist:', err);
      return currentlyInWishlist;
    }
  };

  return {
    wishlist,
    loading,
    isInWishlist,
    toggleWishlist,
    refetch: fetchWishlist,
  };
};
